import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, CreditCard, Crown, LogOut, AlertCircle, Package, ExternalLink, Check, Loader, X } from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { auth, db } from '../config/firebase';
import { signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { FIRESTORE_PATHS, SUBSCRIPTION_CONSTANTS, IS_STRIPE_TEST_MODE } from '../config/appConfig';
import { useToast } from '../contexts/ToastContext';
import logger from '../utils/logger';

const AccountSettings = () => {
  const navigate = useNavigate();
  const { user, userProfile, userSubscription, updateSubscription, loading: userLoading } = useUser();
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [cancelConfirm, setCancelConfirm] = useState(null); // { show, success }
  const { success: showSuccess, error: showError } = useToast();

  const currentPlan = userSubscription?.subscriptionPlan
    ? SUBSCRIPTION_CONSTANTS.getPlanByDbId(userSubscription.subscriptionPlan)
    : null;

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      logger.error('AccountSettings', 'Error signing out:', error);
    }
  };

  const handleChangePlan = () => {
    navigate('/select-plan');
  };

  const handleCancelSubscription = async () => {
    setLoading(true);
    try {
      // If there's an active Stripe subscription, cancel it through Stripe
      // The webhook will handle updating Firestore when Stripe confirms cancellation
      if (userSubscription?.stripeSubscriptionId) {
        const functions = getFunctions();
        const cancelSubscription = httpsCallable(functions, 'cancelSubscription');
        await cancelSubscription({
          subscriptionId: userSubscription.stripeSubscriptionId,
          isTestMode: IS_STRIPE_TEST_MODE || false,
        });
        setCancelConfirm({ success: true });
      } else {
        // No Stripe subscription (free plan or legacy) — downgrade directly
        await updateSubscription('basic', 'inactive');
        setCancelConfirm({ success: true });
      }
    } catch (error) {
      logger.error('AccountSettings', 'Error cancelling subscription:', error);
      // Fallback: update Firestore directly if Cloud Function fails
      try {
        await updateSubscription('basic', 'inactive');
        setCancelConfirm({ success: true });
      } catch (fallbackError) {
        showError('Failed to cancel subscription. Please contact support.');
        setCancelConfirm(null);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's Shopify orders
  useEffect(() => {
    const fetchOrders = async () => {
      if (!user?.email) {
        setOrdersLoading(false);
        return;
      }

      try {
        setOrdersLoading(true);
        // Query orders by user email (email matching strategy)
        const ordersRef = collection(db, FIRESTORE_PATHS.ORDERS);
        const q = query(
          ordersRef,
          where('email', '==', user.email),
          orderBy('orderDate', 'desc')
        );

        logger.debug('[AccountSettings] Fetching orders for email:', user.email);
        const snapshot = await getDocs(q);
        const ordersList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        logger.debug('[AccountSettings] Found orders:', ordersList.length, ordersList);
        setOrders(ordersList);
      } catch (error) {
        logger.error('AccountSettings', 'Error fetching orders:', error);
        // If orderBy fails (no index), try without it
        if (error.code === 'failed-precondition') {
          logger.warn('AccountSettings', 'Order index missing, fetching without orderBy');
          try {
            const ordersRef = collection(db, FIRESTORE_PATHS.ORDERS);
            const q = query(ordersRef, where('email', '==', user.email));
            const snapshot = await getDocs(q);
            const ordersList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            })).sort((a, b) => {
              // Sort by orderDate descending manually
              const dateA = a.orderDate?.toDate?.() || new Date(a.orderDate || 0);
              const dateB = b.orderDate?.toDate?.() || new Date(b.orderDate || 0);
              return dateB - dateA;
            });
            logger.debug('[AccountSettings] Found orders (without index):', ordersList.length);
            setOrders(ordersList);
          } catch (retryError) {
            logger.error('AccountSettings', 'Retry failed:', retryError);
            setOrders([]);
          }
        } else {
          setOrders([]);
        }
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [user?.email]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <div className="max-w-4xl mx-auto flex items-center">
          <h1 className="text-2xl font-bold">Account Settings</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-6 space-y-6">
        {/* Profile Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <User size={20} className="mr-2" />
            Profile Information
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Name</label>
              <p className="text-white font-medium">{userProfile?.displayName || user?.displayName || 'N/A'}</p>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Email</label>
              <p className="text-white font-medium">{user?.email || 'N/A'}</p>
            </div>

            <div>
              <label className="block text-sm text-gray-400 mb-1">Member Since</label>
              <p className="text-white font-medium">
                {userProfile?.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Subscription Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <CreditCard size={20} className="mr-2" />
            Subscription
          </h2>

          {currentPlan ? (
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Crown size={20} className="text-green-500" />
                    <h3 className="text-xl font-bold text-white">{currentPlan.name}</h3>
                  </div>
                  {currentPlan.isFree ? (
                    <p className="text-3xl font-bold text-green-500">Free</p>
                  ) : (
                    <p className="text-3xl font-bold text-green-500">
                      ${userSubscription?.billingCycle === 'yearly' && currentPlan.priceYearly ? currentPlan.priceYearly : currentPlan.price}
                      <span className="text-sm text-gray-400">/{userSubscription?.billingCycle === 'yearly' ? 'year' : 'month'}</span>
                    </p>
                  )}
                  <p className="text-sm text-gray-400 mt-1">
                    Status: <span className={`font-semibold capitalize ${userSubscription?.subscriptionStatus === 'active' ? 'text-green-500' :
                        userSubscription?.subscriptionStatus === 'inactive' ? 'text-gray-400' :
                          userSubscription?.subscriptionStatus === 'cancelled' ? 'text-yellow-500' :
                            'text-red-500'
                      }`}>
                      {userSubscription?.subscriptionPlan === 'basic'
                        ? 'Free Plan'
                        : userSubscription?.subscriptionStatus || 'Unknown'}
                    </span>
                  </p>
                  {userSubscription?.subscriptionStatus === 'cancelled' && userSubscription?.currentPeriodEnd && (
                    <p className="text-sm text-yellow-400 mt-1">
                      Access until: {new Date(userSubscription.currentPeriodEnd?.seconds ? userSubscription.currentPeriodEnd.seconds * 1000 : userSubscription.currentPeriodEnd).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>

              <div className="bg-gray-900 rounded-lg p-4 mb-4">
                <h4 className="font-semibold mb-2 text-sm text-gray-400">Plan Features:</h4>
                <ul className="space-y-1">
                  {((userSubscription?.billingCycle === 'yearly' && currentPlan.featuresYearly) ? currentPlan.featuresYearly : currentPlan.features).map((feature, index) => (
                    <li key={index} className="text-sm text-gray-300 flex items-start gap-2">
                      <Check size={14} className="text-green-500 mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={handleChangePlan}
                  className="flex-1 bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-4 rounded-lg transition-colors"
                >
                  {userSubscription?.subscriptionPlan === 'basic' ? 'Choose a Plan' :
                    userSubscription?.subscriptionStatus === 'cancelled' ? 'Re-subscribe' : 'Change Plan'}
                </button>
                {userSubscription?.subscriptionStatus === 'active' && userSubscription?.subscriptionPlan !== 'basic' && (
                  <button
                    onClick={() => setCancelConfirm({ show: true })}
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            </div>
          ) : userLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading subscription...</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">You don't have an active subscription</p>
              <button
                onClick={handleChangePlan}
                className="bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-colors"
              >
                Choose a Plan
              </button>
            </div>
          )}
        </div>

        {/* My Orders Section */}
        <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h2 className="text-xl font-bold mb-4 flex items-center">
            <Package size={20} className="mr-2" />
            My Orders
          </h2>

          {ordersLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500 mx-auto mb-4"></div>
              <p className="text-gray-400">Loading orders...</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8">
              <Package size={48} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">You haven't placed any orders yet</p>
              <a
                href={`https://cricketxtreme.myshopify.com?checkout[email]=${encodeURIComponent(user?.email || '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block bg-green-600 hover:bg-green-500 text-white font-bold py-2 px-4 rounded-lg transition-colors text-sm"
              >
                Shop Now
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => (
                <div
                  key={order.id}
                  className="bg-gray-900 rounded-lg p-4 border border-gray-700/50 hover:border-gray-600 transition-colors"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-white font-semibold">
                        Order #{order.orderNumber || order.shopifyOrderId}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {order.orderDate?.toDate
                          ? order.orderDate.toDate().toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })
                          : 'Date not available'}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-green-500 font-bold">
                        ${order.totalPrice?.toFixed(2) || '0.00'} {order.currency || 'USD'}
                      </p>
                      <p className={`text-xs mt-1 ${order.fulfillmentStatus === 'fulfilled' ? 'text-green-400' :
                          order.fulfillmentStatus === 'partial' ? 'text-yellow-400' :
                            'text-gray-400'
                        }`}>
                        {order.fulfillmentStatus || 'Processing'}
                      </p>
                    </div>
                  </div>

                  {order.items && order.items.length > 0 && (
                    <div className="mb-3">
                      <p className="text-gray-400 text-sm mb-2">Items:</p>
                      <ul className="space-y-1">
                        {order.items.map((item, index) => (
                          <li key={index} className="text-gray-300 text-sm">
                            • {item.title} × {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {order.shippingAddress && (
                    <div className="pt-3 border-t border-gray-700">
                      <p className="text-gray-400 text-xs mb-1">Shipping to:</p>
                      <p className="text-gray-300 text-sm">
                        {order.shippingAddress.name}<br />
                        {order.shippingAddress.address1}
                        {order.shippingAddress.address2 && `, ${order.shippingAddress.address2}`}
                        <br />
                        {order.shippingAddress.city}, {order.shippingAddress.province} {order.shippingAddress.zip}
                        <br />
                        {order.shippingAddress.country}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Danger Zone */}
        <div className="bg-gray-800 rounded-xl p-6 border border-red-500/30">
          <h2 className="text-xl font-bold mb-4 text-red-500">Danger Zone</h2>

          <button
            onClick={handleLogout}
            className="w-full bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-4 rounded-lg transition-colors flex items-center justify-center"
          >
            <LogOut size={20} className="mr-2" />
            Sign Out
          </button>
        </div>
      </div>

      {/* Cancel Subscription Confirmation Modal */}
      {cancelConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8 max-w-md w-full shadow-2xl">
            {cancelConfirm.success ? (
              // Success state
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-500/20 rounded-full mb-6">
                  <Check size={32} className="text-green-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Subscription Cancelled</h3>
                <p className="text-gray-300 mb-2">
                  Your subscription has been cancelled successfully.
                </p>
                <p className="text-gray-400 text-sm mb-6">
                  You'll continue to have access to all your current features until the end of your billing period. After that, you'll be moved to the free Basic plan.
                </p>
                <button
                  onClick={() => setCancelConfirm(null)}
                  className="w-full bg-green-600 hover:bg-green-500 text-white font-bold py-3 px-6 rounded-lg transition-all"
                >
                  Done
                </button>
              </div>
            ) : (
              // Confirmation state
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/20 rounded-full mb-6">
                  <X size={32} className="text-red-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Cancel your subscription?</h3>
                <p className="text-gray-300 mb-4">
                  You're about to cancel your <span className="text-green-400 font-bold">
                    {SUBSCRIPTION_CONSTANTS.getDisplayName(userSubscription?.subscriptionPlan)}
                  </span> subscription.
                </p>
                <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mb-6 text-left text-sm">
                  <div className="flex items-start gap-2 text-gray-300 mb-2">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>Your access continues until the end of the current billing period</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300 mb-2">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>After that, you'll be moved to the free Basic plan</span>
                  </div>
                  <div className="flex items-start gap-2 text-gray-300">
                    <Check size={16} className="text-green-500 mt-0.5 flex-shrink-0" />
                    <span>You can re-subscribe anytime — billing resumes on the next cycle</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setCancelConfirm(null)}
                    className="flex-1 bg-gray-700 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-lg transition-all border border-gray-600"
                  >
                    Keep Plan
                  </button>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={loading}
                    className="flex-1 bg-red-600 hover:bg-red-500 text-white font-bold py-3 px-6 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <><Loader className="animate-spin h-5 w-5" /> Cancelling...</>
                    ) : (
                      'Confirm Cancel'
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;

